import React, { useEffect, useState, useCallback } from "react";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiClock,
  FiDownload,
  FiEye,
  FiInfo,
  FiLayers,
  FiLock,
  FiMapPin,
  FiPackage,
  FiPlus,
  FiShoppingBag,
  FiX,
  FiFileText,
  FiAlertCircle,
} from "react-icons/fi";
import { RiMoneyDollarBoxLine } from "react-icons/ri";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import axios from "../../Utils/axiosInstance";
import AppLoader from "../../Components/Common/AppLoader";

/* ─── Stage Configuration ──────────────────────────────── */
const STAGE_CONFIG = {
  Order: { label: "Ordering Stage", text: "text-purple-700", bg: "bg-purple-50", border: "border-purple-200" },
  Quotation: { label: "Quotation Stage", text: "text-pink-700", bg: "bg-pink-50", border: "border-pink-200" },
  Sampling: { label: "Sample Stage", text: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200" },
  "Order-Fullfillment": { label: "Fulfillment Stage", text: "text-green-700", bg: "bg-green-50", border: "border-green-200" },
  Delivery_And_Payment: { label: "Delivery & Payment", text: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
  Completed: { label: "Completed", text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
  Closed: { label: "Closed", text: "text-gray-700", bg: "bg-gray-100", border: "border-gray-300" },
};

const getStageStyle = (stage) =>
  STAGE_CONFIG[stage] || {
    label: stage || "Active",
    text: "text-gray-700",
    bg: "bg-gray-100",
    border: "border-gray-200",
  };

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    })
    : "—";

/* ─── Image / Video Modal with Download ────────────────── */
const ImageModal = ({ media, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  if (!media || media.length === 0) return null;

  const goToNext = () =>
    setCurrentIndex((prev) => (prev + 1) % media.length);
  const goToPrev = () =>
    setCurrentIndex((prev) => (prev - 1 + media.length) % media.length);
  const isVideo = (url) =>
    url?.includes(".mp4") || url?.includes(".webm") || url?.includes("video");

  const currentUrl = media[currentIndex];

  const handleDownload = (e) => {
    e.stopPropagation();
    const link = document.createElement("a");
    link.href = currentUrl;
    link.download = `quotation-media-${currentIndex + 1}`;
    link.target = "_blank";
    link.rel = "noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative max-w-5xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute -top-12 left-0 right-0 flex items-center justify-between text-white">
          <span className="text-sm font-medium text-white/80">
            Media {currentIndex + 1} of {media.length}
          </span>
          <div className="flex items-center gap-4">
            <button
              onClick={handleDownload}
              className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition"
            >
              <FiDownload className="text-sm" /> Download
            </button>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors"
            >
              <FiX className="text-2xl" />
            </button>
          </div>
        </div>

        <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-black">
          {isVideo(currentUrl) ? (
            <video
              src={currentUrl}
              controls
              autoPlay
              className="w-full max-h-[75vh] object-contain bg-black"
            />
          ) : (
            <img
              src={currentUrl}
              alt="Quotation Media"
              className="w-full max-h-[75vh] object-contain bg-black"
            />
          )}
        </div>

        {media.length > 1 && (
          <>
            <button
              onClick={goToPrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-[#772037] hover:bg-[#CC234F] text-white rounded-full p-3 border border-white/20 transition-all shadow-lg"
            >
              ❮
            </button>
            <button
              onClick={goToNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-[#772037] hover:bg-[#CC234F] text-white rounded-full p-3 border border-white/20 transition-all shadow-lg"
            >
              ❯
            </button>
          </>
        )}
      </div>
    </div>
  );
};

/* ─── Order Summary Card ───────────────────────────────── */
const OrderSummaryCard = ({ order }) => {
  if (!order) return null;
  const stage = getStageStyle(order.stages);
  const variety = order.productVariety || {};
  const product = order.product || {};
  const category = order.category || {};
  const subCategory = order.subCategory || {};
  const isClosed = order.stages === "Closed";

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Top Banner */}
      <div className="p-4 sm:p-4.5 bg-gradient-to-br from-[#772037] to-[#CC234F] text-white">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[10px] font-bold text-white/50 uppercase tracking-widest mb-0.5">
              ORDER REFERENCE
            </p>
            <h2 className="text-base sm:text-lg font-extrabold tracking-tight">
              {order.orderNumber || `#${order._id?.slice(-6)}`}
            </h2>
            <p className="text-[11px] text-white/60 mt-0.5">
              Placed on {fmtDate(order.createdAt)}
            </p>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-bold border ${stage.bg} ${stage.text} ${stage.border}`}>
            {stage.label}
          </span>
        </div>
      </div>

      {/* Product & Variety Details */}
      <div className="p-4 sm:p-4.5 space-y-3.5">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-gray-50 border border-gray-100 overflow-hidden flex-shrink-0 flex items-center justify-center">
            {variety.img || product.img ? (
              <img
                src={variety.img || product.img}
                alt={variety.varietyName || "Variety"}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-2xl">📦</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-gray-900 truncate">
              {product.name || "Product"}
            </h3>
            <p className="text-xs font-bold text-[#772037] truncate mt-0.5">
              {variety.varietyName || order.customVarietyName || "Variety"}
            </p>
            <p className="text-[11px] text-gray-400 truncate mt-0.5">
              {category.name || ""} {subCategory.name ? `› ${subCategory.name}` : ""}
            </p>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-3 grid grid-cols-2 gap-2 text-center">
          <div className="bg-gray-50 p-2.5 rounded-xl border border-gray-100">
            <p className="text-[10px] font-bold text-gray-400 uppercase">Order Quantity</p>
            <p className="text-xs sm:text-sm font-extrabold text-gray-800 mt-0.5">
              {order.priceAndQuantity?.MOQ?.toLocaleString() || 0}{" "}
              {order.priceAndQuantity?.standardizedUnit || variety.standardizedUNIT || "kg"}
            </p>
          </div>
          <div className="bg-gray-50 p-2.5 rounded-xl border border-gray-100">
            <p className="text-[10px] font-bold text-gray-400 uppercase">Price Range</p>
            <p className="text-xs sm:text-sm font-extrabold text-[#772037] mt-0.5">
              ₹ {order.priceAndQuantity?.expectedPriceRange?.min || 0} – ₹{" "}
              {order.priceAndQuantity?.expectedPriceRange?.max || 0}
            </p>
          </div>
        </div>

        {/* Closed Order Warning */}
        {isClosed && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2 text-red-700">
            <FiLock className="text-base flex-shrink-0 mt-0.5 text-red-600" />
            <div>
              <p className="text-xs font-bold">Order Closed</p>
              <p className="text-[11px] text-red-600 mt-0.5 leading-snug">
                This order has been closed. No new quotations can be created.
              </p>
            </div>
          </div>
        )}

        {/* Quotation Session Ended Warning */}
        {!isClosed && order.stages && !["Order", "Quotation"].includes(order.stages) && (
          <div className="p-3 bg-blue-50/80 border border-blue-200 rounded-xl flex items-start gap-2 text-blue-900">
            <FiInfo className="text-base flex-shrink-0 mt-0.5 text-blue-600" />
            <div>
              <p className="text-xs font-bold text-blue-950">Quotation Session Ended</p>
              <p className="text-[11px] text-blue-800 mt-0.5 leading-snug">
                Quotation adding session has been passed for this order. Buyer has moved to the next stages.
              </p>
            </div>
          </div>
        )}

        {/* Order Under Review Warning */}
        {order.review?.status === "Pending" && (
          <div className="p-3 bg-amber-50/80 border border-amber-200 rounded-xl flex items-start gap-2 text-amber-900">
            <FiClock className="text-base flex-shrink-0 mt-0.5 text-amber-600" />
            <div>
              <p className="text-xs font-bold text-amber-950">Order Under Review</p>
              <p className="text-[11px] text-amber-800 mt-0.5 leading-snug">
                This order is currently under review by QQT. Quotations can be submitted once approved.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Guidelines Tips Card ─────────────────────────────── */
const QuotationGuidelinesCard = () => (
  <div className="bg-amber-50/70 border border-amber-200/80 rounded-2xl p-4 text-amber-900">
    <div className="flex items-center gap-2 mb-2">
      <FiInfo className="text-amber-600 text-base flex-shrink-0" />
      <h4 className="text-xs font-bold uppercase tracking-wider text-amber-800">
        Quotation Guidelines
      </h4>
    </div>
    <ul className="space-y-1.5 text-[11px] sm:text-xs text-amber-900/90 pl-1 leading-relaxed">
      <li className="flex items-start gap-1.5">
        <span className="text-amber-500 font-bold">•</span>
        <span>You can submit a maximum of <strong>3 active quotations</strong> for this order.</span>
      </li>
      <li className="flex items-start gap-1.5">
        <span className="text-amber-500 font-bold">•</span>
        <span>Pending quotations count toward the limit until approved or rejected.</span>
      </li>
      <li className="flex items-start gap-1.5">
        <span className="text-amber-500 font-bold">•</span>
        <span>Rejected quotations do not count and allow submitting a revised quote.</span>
      </li>
    </ul>
  </div>
);

/* ─── Quotation Card Component ─────────────────────────── */
const SupplierQuotationCard = ({ quotation, index, onSelect }) => {
  const isSelected = quotation.isSelectedByBuyer || quotation.status === "Selected";
  const reviewStatus = quotation.review?.status || "Pending";
  const impressions = quotation.ofm_Impressions || [];
  const isFulfilled = impressions.some((imp) => imp.status === "Fulfilled");
  const lastImpression = impressions[impressions.length - 1];
  const type = lastImpression?.this_is_for || "Full_Order";

  const totalAmount = (quotation.pricing?.amount || 0) * (quotation.quantity?.volume || 0);

  return (
    <div
      onClick={() => onSelect(quotation)}
      className={`relative rounded-2xl border transition-all duration-200 cursor-pointer overflow-hidden p-3.5 sm:p-5 shadow-xs hover:shadow-md ${isSelected
        ? "border-emerald-500 bg-emerald-50/20"
        : "border-gray-200 bg-white hover:border-[#772037]/50"
        }`}
    >
      {/* Top Banner Indicator */}
      <div className="flex items-start justify-between gap-2 mb-2.5">
        <div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] font-bold text-gray-400">
              Quotation #{index + 1}
            </span>
            <span className="text-xs sm:text-sm font-extrabold text-gray-900">
              {quotation.quotationUniqueNumber || `#${quotation._id?.slice(-6)}`}
            </span>
          </div>
          <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
            <FiClock className="text-[10px]" /> Submitted {fmtDate(quotation.createdAt)}
          </p>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          {isFulfilled && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200">
              Ready ({type})
            </span>
          )}

          {isSelected && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
              <FiCheckCircle className="text-[10px]" /> Selected
            </span>
          )}

          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${reviewStatus === "Approved"
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : reviewStatus === "Rejected"
                ? "bg-red-50 text-red-700 border-red-200"
                : "bg-amber-50 text-amber-700 border-amber-200"
              }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${reviewStatus === "Approved"
              ? "bg-emerald-500"
              : reviewStatus === "Rejected"
                ? "bg-red-500"
                : "bg-amber-500 animate-pulse"
              }`} />
            {reviewStatus}
          </span>
        </div>
      </div>

      {/* Pricing & Quantity 3-Column Grid */}
      <div className="grid grid-cols-2 gap-1.5 sm:gap-3 p-2.5 sm:p-3 rounded-xl bg-gray-50/80 border border-gray-100 text-center">
        <div>
          <p className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase tracking-tight truncate">Quoted Price</p>
          <p className="text-xs sm:text-base font-extrabold text-[#772037] mt-0.5 truncate">
            ₹ {quotation.pricing?.amount?.toLocaleString() || 0}
          </p>
          <p className="text-[9px] sm:text-[10px] text-gray-400 truncate">per {quotation.standardizedUnit || "Unit"}</p>
        </div>

        <div className="border-x border-gray-200/80 px-1">
          <p className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase tracking-tight truncate">Quantity</p>
          <p className="text-xs sm:text-base font-extrabold text-gray-800 mt-0.5 truncate">
            {quotation.quantity?.volume?.toLocaleString() || 0} {quotation.standardizedUnit || ""}
          </p>
          <p className="text-[9px] sm:text-[10px] text-gray-400 truncate">Volume</p>
        </div>

        {/* <div>
          <p className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase tracking-tight truncate">Estimated Total</p>
          <p className="text-xs sm:text-base font-extrabold text-gray-800 mt-0.5 truncate">
            ₹ {totalAmount.toLocaleString()}
          </p>
          <p className="text-[9px] sm:text-[10px] text-gray-400 truncate">Subtotal</p>
        </div> */}
      </div>

      {/* Meta Footer */}
      <div className="mt-2.5 flex items-center justify-between text-xs pt-2 border-t border-gray-100">
        <span className="text-[11px] text-gray-400 font-medium truncate">
          {quotation.batch_Lot_Number ? `Batch: ${quotation.batch_Lot_Number}` : "Standard Batch"}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSelect(quotation);
          }}
          className="text-xs font-bold text-[#772037] hover:text-[#CC234F] flex items-center gap-1 transition flex-shrink-0"
        >
          <FiEye className="text-xs" /> View Details →
        </button>
      </div>
    </div>
  );
};

/* ─── Main Supplier Quotation List Component ───────────── */
const SupplierQuotationList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const orderIdParam = queryParams.get("orderId");

  const [order, setOrder] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);

  // Fetch Order & Quotations matching this order via v2 matching endpoint
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);

      if (orderIdParam) {
        // Fetch specific order details and its submitted quotations with matching v2
        const [orderRes, quoteRes] = await Promise.allSettled([
          axios.get(`/order/get-order-by-id/${orderIdParam}`),
          axios.get(`/quotation/get-quotation-matchings-by-order/${orderIdParam}`),
        ]);

        if (orderRes.status === "fulfilled" && orderRes.value.data?.success) {
          setOrder(orderRes.value.data.data);
        }

        if (quoteRes.status === "fulfilled" && quoteRes.value.data?.success) {
          const list = quoteRes.value.data.data?.quotations || quoteRes.value.data.data || [];
          setQuotations(Array.isArray(list) ? list : []);
        } else {
          setQuotations([]);
        }
      } else {
        // Fallback: Fetch general paginated quotations if no orderId
        const { data } = await axios.get(`/quotation/get-all-paginated-data?page=1&limit=20`);
        if (data.success) {
          const list = data.data?.quotations || data.data || [];
          setQuotations(Array.isArray(list) ? list : []);
        }
      }
    } catch (err) {
      console.error("Error fetching supplier quotations:", err);
      setError(true);
      toast.error("Failed to load quotations");
    } finally {
      setLoading(false);
    }
  }, [orderIdParam]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Validation for creating new quotation
  const getDisabledReason = (orderData, quotes, isLoading) => {
    if (isLoading) return "Loading order details...";
    if (!orderData) return "Loading order details...";
    const reviewStatus = orderData.review?.status || "Pending";
    const isOrderRejected = reviewStatus === "Rejected";
    const isOrderPending = reviewStatus === "Pending";
    const allowedStages = ["Order", "Quotation"];
    const isValidStage = !orderData.stages || allowedStages.includes(orderData.stages);
    const activeCount = (quotes || []).filter(
      (q) => q.review?.status === "Approved" || q.review?.status === "Pending"
    ).length;
    const isLimitReached = activeCount >= 3;

    if (orderData.stages === "Closed") {
      return "This order has been closed. No new quotations can be created for this order.";
    }
    if (isOrderRejected) {
      return "This order has been rejected by admin. Quotation is not allowed.";
    }
    if (isOrderPending) {
      return "This order is under review. You can create quotation once approved by QQT.";
    }
    if (!isValidStage) {
      return "Quotation adding session has been passed for this order.";
    }
    if (isLimitReached) {
      return "You have already created 3 quotations for this order.";
    }
    return "";
  };

  const disabledReason = getDisabledReason(order, quotations, loading);
  const canCreateQuotation = !loading && !disabledReason;

  const handleCreateQuotation = () => {
    if (!canCreateQuotation) {
      toast.error(disabledReason, {
        duration: 3500,
        icon: "⚠️",
      });
      return;
    }
    if (orderIdParam) {
      navigate(`/create-quotation?orderId=${orderIdParam}`);
    } else {
      navigate("/supplier-orders");
    }
  };

  const handleBack = () => {
    if (location.state?.from === "details" && orderIdParam) {
      navigate(`/supplier-orders?orderId=${orderIdParam}`);
    } else if (location.state?.from === "card") {
      navigate("/supplier-orders");
    } else if (orderIdParam) {
      navigate(`/supplier-orders?orderId=${orderIdParam}`);
    } else {
      navigate("/supplier-orders");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-36 sm:pb-24">
      <div className="2xl:max-w-7xl mx-auto px-2.5 sm:px-4 md:px-8 py-3 sm:py-6">
        {/* Navigation & Header */}
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <button
              onClick={handleBack}
              className="w-9 h-9 rounded-xl bg-white border border-gray-200 hover:border-primaryColor text-gray-700 hover:text-primaryColor flex items-center justify-center transition shadow-xs"
              title="Back"
            >
              <FiArrowLeft className="text-base" />
            </button>
            <div>
              <h1 className="text-lg sm:text-2xl font-bold text-primaryColor flex items-center gap-2">
                <RiMoneyDollarBoxLine className="text-xl sm:text-2xl" /> Your Quotations
              </h1>
              <p className="text-gray-500 text-xs sm:text-sm mt-0.5">
                {order?.orderNumber
                  ? `Quotations submitted for Order ${order.orderNumber}`
                  : "Review and manage quotations submitted to buyers."}
              </p>
            </div>
          </div>

          <div className="hidden sm:flex items-center gap-2">
            <button
              onClick={handleCreateQuotation}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs sm:text-sm font-bold rounded-xl transition shadow-sm ${canCreateQuotation
                ? "bg-primaryColor text-white hover:bg-primaryColor/90 shadow-primaryColor/20"
                : "bg-gray-200 text-gray-500 hover:bg-gray-300 border border-gray-300 cursor-pointer"
                }`}
            >
              <FiPlus className="text-base" /> Create New Quotation
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && <AppLoader message="Loading Quotations..." />}

        {/* Error State */}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <FiAlertCircle className="w-12 h-12 text-red-500 mb-3" />
            <p className="text-red-500 font-medium mb-3">Failed to load order quotations</p>
            <button
              onClick={fetchData}
              className="px-4 py-2 bg-primaryColor text-white rounded-xl text-sm font-semibold hover:bg-primaryColor/90 shadow-sm"
            >
              Retry
            </button>
          </div>
        )}

        {/* Content Layout */}
        {!loading && !error && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
            {/* ── LEFT COLUMN (Order Details & Guidelines on Large Screen, Top on Mobile) ── */}
            <div className="lg:col-span-4 lg:sticky lg:top-20 space-y-4">
              {order ? (
                <>
                  <OrderSummaryCard order={order} />
                  <QuotationGuidelinesCard />
                </>
              ) : (
                <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm text-center">
                  <FiShoppingBag className="w-10 h-10 text-primaryColor/40 mx-auto mb-2" />
                  <h3 className="text-sm font-bold text-gray-800">Browse Live Orders</h3>
                  <p className="text-xs text-gray-400 mt-1 mb-4">
                    Select an order from the live marketplace to view and submit quotation proposals.
                  </p>
                  <button
                    onClick={() => navigate("/supplier-orders")}
                    className="w-full py-2 px-3 rounded-xl bg-primaryColor text-white text-xs font-bold hover:bg-primaryColor/90 transition shadow-sm"
                  >
                    View Live Orders
                  </button>
                </div>
              )}
            </div>

            {/* ── RIGHT COLUMN (Submitted Quotations List) ──────────────────────── */}
            <div className="lg:col-span-8 space-y-4">
              {/* Buyer Selection Note matching Mobile App */}
              {quotations.some((q) => q.isSelectedByBuyer || q.status === "Selected") && (
                <div className="p-3.5 bg-emerald-50/80 border border-emerald-200 rounded-2xl flex items-start gap-2.5 text-emerald-900 shadow-xs">
                  <FiCheckCircle className="text-base flex-shrink-0 mt-0.5 text-emerald-600" />
                  <div>
                    <p className="text-xs font-bold text-emerald-950">Quotation Selected by Buyer</p>
                    <p className="text-[11px] text-emerald-800 mt-0.5 leading-snug">
                      Buyer has selected a quotation based on their requirements. Please proceed with the next steps accordingly.
                    </p>
                  </div>
                </div>
              )}

              {/* Context Header */}
              <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-xs flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-[#772037]/10 flex items-center justify-center text-[#772037]">
                    <FiFileText className="text-base" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">
                      Submitted Quotations ({quotations.length})
                    </h3>
                    <p className="text-[11px] text-gray-400">
                      All proposals you have submitted for this requirement
                    </p>
                  </div>
                </div>

                <span className="text-xs font-bold text-[#772037] bg-[#772037]/10 px-2.5 py-1 rounded-lg">
                  {quotations.length} Quotations
                </span>
              </div>

              {/* Quotation Cards List */}
              {quotations.length > 0 ? (
                <div className="space-y-3.5">
                  {quotations.map((q, i) => (
                    <SupplierQuotationCard
                      key={q._id || i}
                      quotation={q}
                      index={i}
                      onSelect={(item) => {
                        navigate(
                          `/supplier-quotation-details?quotationId=${item._id}&orderId=${orderIdParam || order?._id}`,
                          {
                            state: {
                              quotation: item,
                              order: order || item.orderId,
                              from: location.state?.from || "list",
                            },
                          }
                        );
                      }}
                    />
                  ))}
                </div>
              ) : (
                /* Empty State */
                <div className="bg-white rounded-2xl border border-gray-100 p-8 sm:p-12 text-center shadow-xs">
                  <div className="w-16 h-16 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center mx-auto mb-4 text-gray-300">
                    <FiFileText className="text-3xl" />
                  </div>
                  <h3 className="text-base font-bold text-gray-800">No Quotations Submitted Yet</h3>
                  <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto mb-4">
                    {canCreateQuotation
                      ? "You haven't submitted any quotations for this order requirement yet. Review the details and submit your best price offer."
                      : "Quotation submissions are unavailable or closed for this order requirement."}
                  </p>
                  {canCreateQuotation && (
                    <button
                      onClick={handleCreateQuotation}
                      className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition shadow-sm bg-primaryColor text-white hover:bg-primaryColor/90 shadow-primaryColor/20"
                    >
                      <FiPlus className="text-base" /> Create First Quotation
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── MOBILE FIXED BOTTOM ACTION / STATUS BAR ──────────────────────────── */}
      {!loading && (
        <div className="sm:hidden fixed bottom-0 left-0 right-0 p-2.5 bg-white/95 backdrop-blur-md border-t border-gray-200 z-30 shadow-lg">
          {canCreateQuotation ? (
            <button
              onClick={handleCreateQuotation}
              className="w-full py-3 px-4 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition shadow-sm bg-primaryColor text-white hover:bg-primaryColor/90 shadow-primaryColor/20"
            >
              <FiPlus className="text-base" /> Create New Quotation
            </button>
          ) : (
            <button
              onClick={handleCreateQuotation}
              className="w-full py-2.5 px-4 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 transition shadow-xs bg-gray-100 text-gray-600 border border-gray-200 active:bg-gray-200 cursor-pointer"
            >
              <FiInfo className="text-sm text-gray-500" />
              <span className="truncate">{disabledReason || "Quotation Session Over"}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SupplierQuotationList;
